import React, { useState, useRef, useEffect } from "react";
import s from "./Accordion.module.scss";
import { ConsultationType } from "@/pages/MainPage/ui/MyProducts/ui/ConsultationType/ConsultationType";
import { ConsultationCost } from "@/pages/MainPage/ui/MyProducts/ui/ConsultationCost/ConsultationCost";
import { AssessmentBy } from "@/pages/MainPage/ui/MyProducts/ui/AssessmentBy/AssessmentBy";
import { AssessmentWho } from "@/pages/MainPage/ui/MyProducts/ui/AssessmentWho/AssessmentWho";
import { AssessmentFor } from "@/pages/MainPage/ui/MyProducts/ui/AssessmentFor/AssessmentFor";
import { AssessmentMental } from "@/pages/MainPage/ui/MyProducts/ui/AssessmentMental/AssessmentMental";
import { AssessmentProblems } from "@/pages/MainPage/ui/MyProducts/ui/AssessmentProblems/AssessmentProblems";
import { AssessmentTension } from "@/pages/MainPage/ui/MyProducts/ui/AssessmentTension/AssessmentTension";
import { AssessmentProcess } from "@/pages/MainPage/ui/MyProducts/ui/AssessmentProcess/AssessmentProcess";
import { classNames } from "@/shared/lib/classNames/classNames";
import { ArrowDown } from "@/pages/MainPage/ui/assets/icons/arrow-down";
import { AssessmentGet } from "@/pages/MainPage/ui/MyProducts/ui/AssessmentGet/AssessmentGet";
import { AssessmentTypes } from "@/pages/MainPage/ui/MyProducts/ui/AssessmentTypes/AssessmentTypes";
import { Download } from "@/pages/MainPage/ui/MyProducts/ui/Download/Download";

type Item = {
    id: number;
    title: string;
    content: React.ReactNode;
};

const items: Item[] = [
    {
        id: 1,
        title: "vip personal Consultation",
        content: (
            <>
                <ConsultationType />
                <ConsultationCost />
            </>
        ),
    },
    {
        id: 2,
        title: "Psychophysical Constitution Assessment",
        content: (
            <>
                <AssessmentBy />
                <AssessmentWho />
                <AssessmentFor />
                <AssessmentMental />
                <AssessmentProblems />
                <AssessmentTension />
                <AssessmentProcess />
                <AssessmentGet />
                <AssessmentTypes />
            </>
        ),
    },
    {
        id: 3,
        title: "health education guide",
        content: <Download />,
    },
];

export const Accordion = () => {
    const [openId, setOpenId] = useState<number | null>(null);
    const contentRefs = useRef<Record<number, HTMLDivElement | null>>({});

    // Автоматически подстраиваем высоту при ресайзе (чтобы анимация не ломалась)
    useEffect(() => {
        const onResize = () => {
            if (openId != null && contentRefs.current) {
                const el = contentRefs.current[openId];
                if (el) {
                    el.style.height = el.scrollHeight + "px";
                    const transitionEnd = () => {
                        el.style.height = "auto";
                        el.removeEventListener("transitionend", transitionEnd);
                    };
                    el.addEventListener("transitionend", transitionEnd);
                }
            }
        };
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, [openId]);

    const toggle = (id: number) => {
        if (!contentRefs.current) {
            return;
        }
        const isOpening = openId !== id;
        // Сначала закрываем текущий
        if (openId != null && contentRefs.current[openId]) {
            contentRefs.current[openId]!.style.height =
                contentRefs.current[openId]!.scrollHeight + "px";
            // принудительный reflow для корректной анимации закрытия
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            contentRefs.current[openId]!.offsetHeight;
            contentRefs.current[openId]!.style.height = "0px";
        }
        // Открываем новый
        if (isOpening) {
            requestAnimationFrame(() => {
                const el = contentRefs.current?.[id];
                if (el) {
                    el.style.height = el.scrollHeight + "px";
                }
                setOpenId(id);
            });
        } else {
            setOpenId(null);
        }
    };

    return (
        <div className={s.accordion} role="list">
            {items.map((item) => {
                const expanded = openId === item.id;
                return (
                    <div className={s.item} key={item.id} role="listitem">
                        <button
                            className={classNames(s.trigger, {
                                [s.expanded]: expanded,
                            })}
                            onClick={() => toggle(item.id)}
                            aria-expanded={expanded}
                            aria-controls={`content-${item.id}`}
                        >
                            <span className={s.title}>{item.title}</span>
                            <div
                                className={`${s.chevron} ${expanded ? s.chevronOpen : ""}`}
                            >
                                <ArrowDown />
                            </div>
                        </button>

                        <div
                            id={`content-${item.id}`}
                            className={s.contentWrapper}
                            ref={(el) => {
                                contentRefs.current[item.id] = el;
                            }}
                            role="region"
                            aria-hidden={!expanded}
                        >
                            {item.content}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};
